import React from 'react'
import { useDispatch } from 'react-redux'
import {
  LeaseCompanyBranch,
  LeaseCompanyComponent,
  LeaseCompanyContractModal,
  LeaseCompanyCreateServiceOrder,
  LeaseCompanyCreateServiceOrderLine,
  LeaseCompanyCreateUserData,
  LeaseCompanyDeleteUser,
  LeaseCompanyFixedPrice,
  LeaseCompanyFixedPriceModal,
  LeaseCompanyInvoiceSummaryModal,
  LeaseCompanySearchModal,
  LeaseCompanyServiceOrderLineModal,
  LeaseCompanyServiceOrderModal,
  LeaseCompanyServiceOrderStatusModal,
  LeaseCompanyTyreModal,
  LeaseCompanyTyreSearchModal,
  LeaseCompanyUpdateServiceOrderData,
  LeaseCompanyUpdateServiceOrderLine,
  LeaseCompanyUsersModal
} from '@cbgms/api/modules/lease-company'
import {
  createServiceOrder,
  createServiceOrderLine,
  createUser,
  deleteServiceOrderLine,
  deleteUser,
  existDefaultUser,
  existServiceOrder,
  getAllBranchFormulas,
  getBranchFormulaByUser,
  getComponent,
  getContract,
  getDefaultUser,
  getFixedPrice,
  getInvoiceSummary,
  getSearch,
  getSearchForTyres,
  getServiceOrder,
  getServiceOrderLine,
  getServiceOrderLines,
  getServiceOrderStatuses,
  getSpecificTyre,
  getUser,
  getUsers,
  publishServiceOrder,
  setDefaultUser,
  testUser,
  updateServiceOrder,
  updateServiceOrderLine,
  updateServiceOrderLineReference,
  updateUser
} from './actions'
import { IPaginatedRequest, TyreType } from '@cbgms/api'

interface LeaseCompanyService {
  getContract(workorderUUID: string): Promise<LeaseCompanyContractModal>
  getUsers(): Promise<LeaseCompanyUsersModal[]>
  createUser(data: LeaseCompanyCreateUserData): Promise<{ UUID: string }>
  updateUser(userUUID: string, data: LeaseCompanyCreateUserData): Promise<{ UUID: string }>
  getUser(userUUID: string): Promise<LeaseCompanyUsersModal>
  deleteUser(userUUID: string): Promise<LeaseCompanyDeleteUser>
  testUser(data: LeaseCompanyCreateUserData): Promise<any>
  getAllBranchFormulas(): Promise<LeaseCompanyBranch[]>
  getBranchFormulaByUser(userUUID: string): Promise<LeaseCompanyBranch>
  existDefaultUser(): Promise<{ Exists: boolean }>
  getDefaultUser(): Promise<LeaseCompanyUsersModal>
  setDefaultUser(userUUID: string): Promise<{ UUID: string }>
  getSearch(workorderUUID: string, params: IPaginatedRequest): Promise<LeaseCompanySearchModal>
  getComponent(workorderUUID: string, code: string): Promise<LeaseCompanyComponent>
  existServiceOrder(workorderUUID: string): Promise<{ Exists: boolean }>
  getServiceOrder(workorderUUID: string): Promise<LeaseCompanyServiceOrderModal>
  createServiceOrder(workorderUUID: string, data: LeaseCompanyCreateServiceOrder): Promise<{ UUID: string }>
  updateServiceOrder(workorderUUID: string, data: LeaseCompanyUpdateServiceOrderData): Promise<{ UUID: string }>
  publishServiceOrder(workorderUUID: string): Promise<{ UUID: string }>
  getServiceOrderLines(workorderUUID: string): Promise<LeaseCompanyServiceOrderLineModal[]>
  createServiceOrderLine(workorderUUID: string, data: LeaseCompanyCreateServiceOrderLine): Promise<{ UUID: string }>
  updateServiceOrderLine(
    workorderUUID: string,
    workorderLineUUID: string,
    data: LeaseCompanyUpdateServiceOrderLine
  ): Promise<{ UUID: string }>
  updateServiceOrderLineReference(serviceOrderUUID: string, serviceOrderLineUUID: string, data: { ReferenceUUID: string }): Promise<void>
  getServiceOrderLine(workorderUUID: string, workorderLineUUID: string): Promise<LeaseCompanyServiceOrderLineModal>
  deleteServiceOrderLine(workorderUUID: string, workorderLineUUID: string): Promise<{ LineDeleted: boolean }>
  getSearchForTyres(workorderUUID: string, params: IPaginatedRequest & { season?: TyreType }): Promise<LeaseCompanyTyreSearchModal>
  getSpecificTyre(workorderUUID: string, id: string): Promise<LeaseCompanyTyreModal>
  getServiceOrderStatuses(): Promise<LeaseCompanyServiceOrderStatusModal[]>
  getFixedPrice(workorderUUID: string, data: LeaseCompanyFixedPrice): Promise<LeaseCompanyFixedPriceModal[]>
  getInvoiceSummary(workorderUUID: string): Promise<LeaseCompanyInvoiceSummaryModal>
}

export function useLeaseCompanyService(): LeaseCompanyService {
  const dispatch = useDispatch()

  const getLeaseCompanyContractFunc = React.useCallback(
    async (workorderUUID: string) => {
      const { Data } = await dispatch(getContract(workorderUUID))
      return Data
    },
    [dispatch]
  )

  const getLeaseCompanyUsersFunc = React.useCallback(async () => {
    const { Data } = await dispatch(getUsers())
    return Data
  }, [dispatch])

  const createLeaseCompanyUserFunc = React.useCallback(
    async (data: LeaseCompanyCreateUserData) => {
      const { Data } = await dispatch(createUser(data))
      return Data
    },
    [dispatch]
  )

  const updateLeaseCompanyUserFunc = React.useCallback(
    async (userUUID: string, data: LeaseCompanyCreateUserData) => {
      const { Data } = await dispatch(updateUser(userUUID, data))
      return Data
    },
    [dispatch]
  )

  const getLeaseCompanyUserFunc = React.useCallback(
    async (userUUID: string) => {
      const { Data } = await dispatch(getUser(userUUID))
      return Data
    },
    [dispatch]
  )

  const deleteLeaseCompanyUserFunc = React.useCallback(
    async (userUUID: string) => {
      const { Data } = await dispatch(deleteUser(userUUID))
      return Data
    },
    [dispatch]
  )

  const testLeaseCompanyUserFunc = React.useCallback(
    async (data: LeaseCompanyCreateUserData) => {
      const { Data } = await dispatch(testUser(data))
      return Data
    },
    [dispatch]
  )

  const getAllBranchFormulasFunc = React.useCallback(async () => {
    const { Data } = await dispatch(getAllBranchFormulas())
    return Data
  }, [dispatch])

  const getBranchFormulaByUserFunc = React.useCallback(
    async (userUUID: string) => {
      const { Data } = await dispatch(getBranchFormulaByUser(userUUID))
      return Data
    },
    [dispatch]
  )

  const existDefaultUserFunc = React.useCallback(async () => {
    const { Data } = await dispatch(existDefaultUser())
    return Data
  }, [dispatch])

  const getDefaultUserFunc = React.useCallback(async () => {
    const { Data } = await dispatch(getDefaultUser())
    return Data
  }, [dispatch])

  const setDefaultUserFunc = React.useCallback(
    async (userUUID: string) => {
      const { Data } = await dispatch(setDefaultUser(userUUID))
      return Data
    },
    [dispatch]
  )

  const searchLeaseCompanyFunc = React.useCallback(
    async (workorderUUID: string, params: IPaginatedRequest) => {
      const { Data } = await dispatch(getSearch(workorderUUID, params))
      return Data
    },
    [dispatch]
  )

  const getLeaseCompanyComponentFunc = React.useCallback(
    async (workorderUUID: string, code: string) => {
      const { Data } = await dispatch(getComponent(workorderUUID, code))
      return Data
    },
    [dispatch]
  )

  const existServiceOrderFunc = React.useCallback(
    async (workorderUUID: string) => {
      const { Data } = await dispatch(existServiceOrder(workorderUUID))
      return Data
    },
    [dispatch]
  )

  const getServiceOrderFunc = React.useCallback(
    async (workorderUUID: string) => {
      const { Data } = await dispatch(getServiceOrder(workorderUUID))
      return Data
    },
    [dispatch]
  )

  const updateServiceOrderFunc = React.useCallback(
    async (workorderUUID: string, data: LeaseCompanyUpdateServiceOrderData) => {
      const { Data } = await dispatch(updateServiceOrder(workorderUUID, data))
      return Data
    },
    [dispatch]
  )

  const publishServiceOrderFunc = React.useCallback(
    async (workorderUUID: string) => {
      const { Data } = await dispatch(publishServiceOrder(workorderUUID))
      return Data
    },
    [dispatch]
  )

  const getServiceOrderLinesFunc = React.useCallback(
    async (workorderUUID: string) => {
      const { Data } = await dispatch(getServiceOrderLines(workorderUUID))
      return Data
    },
    [dispatch]
  )

  const createServiceOrderFunc = React.useCallback(
    async (workorderUUID: string, data: LeaseCompanyCreateServiceOrder) => {
      const { Data } = await dispatch(createServiceOrder(workorderUUID, data))
      return Data
    },
    [dispatch]
  )

  const createServiceOrderLineFunc = React.useCallback(
    async (workorderUUID: string, data: LeaseCompanyCreateServiceOrderLine) => {
      const { Data } = await dispatch(createServiceOrderLine(workorderUUID, data))
      return Data
    },
    [dispatch]
  )

  const updateServiceOrderLineFunc = React.useCallback(
    async (workorderUUID: string, workorderLineUUID: string, data: LeaseCompanyUpdateServiceOrderLine) => {
      const { Data } = await dispatch(updateServiceOrderLine(workorderUUID, workorderLineUUID, data))
      return Data
    },
    [dispatch]
  )

  const updateServiceOrderLineReferenceFunc = React.useCallback<LeaseCompanyService['updateServiceOrderLineReference']>(
    async (serviceOrderUUID, serviceOrderLineUUID, data) => {
      return dispatch(updateServiceOrderLineReference(serviceOrderUUID, serviceOrderLineUUID, data))
    },
    [dispatch]
  )

  const getServiceOrderLineFunc = React.useCallback(
    async (workorderUUID: string, workorderLineUUID: string) => {
      const { Data } = await dispatch(getServiceOrderLine(workorderUUID, workorderLineUUID))
      return Data
    },
    [dispatch]
  )

  const deleteServiceOrderLineFunc = React.useCallback(
    async (workorderUUID: string, workorderLineUUID: string) => {
      const { Data } = await dispatch(deleteServiceOrderLine(workorderUUID, workorderLineUUID))
      return Data
    },
    [dispatch]
  )

  const searchForTyresFunc = React.useCallback(
    async (workorderUUID: string, params: IPaginatedRequest & { season?: TyreType }) => {
      const { Data } = await dispatch(getSearchForTyres(workorderUUID, params))
      return Data
    },
    [dispatch]
  )

  const specificTyreFunc = React.useCallback(
    async (workorderUUID: string, id: string) => {
      const { Data } = await dispatch(getSpecificTyre(workorderUUID, id))
      return Data
    },
    [dispatch]
  )

  const getServiceOrderStatusesFunc = React.useCallback(async () => {
    const { Data } = await dispatch(getServiceOrderStatuses())
    return Data
  }, [dispatch])

  const fixedPriceFunc = React.useCallback(
    async (workorderUUID: string, data: LeaseCompanyFixedPrice) => {
      const result = await dispatch(getFixedPrice(workorderUUID, data))

      return result.Data
    },
    [dispatch]
  )

  const getInvoiceSummaryFunc = React.useCallback(
    async (workorderUUID: string) => {
      const { Data } = await dispatch(getInvoiceSummary(workorderUUID))
      return Data
    },
    [dispatch]
  )

  return {
    getContract: getLeaseCompanyContractFunc,
    getUsers: getLeaseCompanyUsersFunc,
    createUser: createLeaseCompanyUserFunc,
    updateUser: updateLeaseCompanyUserFunc,
    getUser: getLeaseCompanyUserFunc,
    deleteUser: deleteLeaseCompanyUserFunc,
    testUser: testLeaseCompanyUserFunc,
    getAllBranchFormulas: getAllBranchFormulasFunc,
    getBranchFormulaByUser: getBranchFormulaByUserFunc,
    existDefaultUser: existDefaultUserFunc,
    getDefaultUser: getDefaultUserFunc,
    setDefaultUser: setDefaultUserFunc,
    getSearch: searchLeaseCompanyFunc,
    getComponent: getLeaseCompanyComponentFunc,
    existServiceOrder: existServiceOrderFunc,
    getServiceOrder: getServiceOrderFunc,
    updateServiceOrder: updateServiceOrderFunc,
    publishServiceOrder: publishServiceOrderFunc,
    getServiceOrderLines: getServiceOrderLinesFunc,
    createServiceOrder: createServiceOrderFunc,
    createServiceOrderLine: createServiceOrderLineFunc,
    updateServiceOrderLine: updateServiceOrderLineFunc,
    updateServiceOrderLineReference: updateServiceOrderLineReferenceFunc,
    getServiceOrderLine: getServiceOrderLineFunc,
    deleteServiceOrderLine: deleteServiceOrderLineFunc,
    getSearchForTyres: searchForTyresFunc,
    getSpecificTyre: specificTyreFunc,
    getServiceOrderStatuses: getServiceOrderStatusesFunc,
    getFixedPrice: fixedPriceFunc,
    getInvoiceSummary: getInvoiceSummaryFunc
  }
}
